import { generateThemePalette } from '../themeGenerator';
import type { ThemeDefinition as ImportedThemeDefinition } from '../types/theme';

// Export the type so it can be imported by other modules
export type ThemeDefinition = ImportedThemeDefinition;

// Define base colors for our predefined themes
const TERRARIUM_ORANGE = '#FF6B35'; // Our brand orange
const OCEAN_BLUE = '#0077B6';       // A calm, professional blue
const FOREST_GREEN = '#2F855A';     // A natural, trustworthy green
const MINIMAL_GRAY = '#4A5568';     // A neutral, modern gray

// Generate full palettes using the utility
const terrariumOrangePalette = generateThemePalette(TERRARIUM_ORANGE, false);
const terrariumOrangeDarkPalette = generateThemePalette(TERRARIUM_ORANGE, true);

const oceanWhisperPalette = generateThemePalette(OCEAN_BLUE, false);
const oceanWhisperDarkPalette = generateThemePalette(OCEAN_BLUE, true);

const forestRetreatPalette = generateThemePalette(FOREST_GREEN, false);
const forestRetreatDarkPalette = generateThemePalette(FOREST_GREEN, true);

const monochromeModernPalette = generateThemePalette(MINIMAL_GRAY, false);
const monochromeModernDarkPalette = generateThemePalette(MINIMAL_GRAY, true);

export const PREDEFINED_THEMES: ThemeDefinition[] = [
  {
    id: 'terrarium_orange_light',
    name: 'Terrarium Original (Light)',
    description: 'Vibrant and energetic, based on the Terrarium brand orange.',
    isDark: false,
    previewColors: {
      primary: TERRARIUM_ORANGE,
      surface: terrariumOrangePalette.surface,
      background: terrariumOrangePalette.background,
      textBody: terrariumOrangePalette.textBody,
    },
    palette: terrariumOrangePalette,
  },
  {
    id: 'terrarium_orange_dark',
    name: 'Terrarium Original (Dark)',
    description: 'A dark mode take on the vibrant Terrarium brand orange.',
    isDark: true,
    previewColors: {
      primary: TERRARIUM_ORANGE,
      surface: terrariumOrangeDarkPalette.surface,
      background: terrariumOrangeDarkPalette.background,
      textBody: terrariumOrangeDarkPalette.textBody,
    },
    palette: terrariumOrangeDarkPalette,
  },
  {
    id: 'ocean_whisper_light',
    name: 'Ocean Whisper (Light)',
    description: 'Calm and professional blues and grays.',
    isDark: false,
    previewColors: {
      primary: OCEAN_BLUE,
      surface: oceanWhisperPalette.surface,
      background: oceanWhisperPalette.background,
      textBody: oceanWhisperPalette.textBody,
    },
    palette: oceanWhisperPalette,
  },
  {
    id: 'ocean_whisper_dark',
    name: 'Ocean Whisper (Dark)',
    description: 'A deep and focused dark blue theme.',
    isDark: true,
    previewColors: {
      primary: OCEAN_BLUE,
      surface: oceanWhisperDarkPalette.surface,
      background: oceanWhisperDarkPalette.background,
      textBody: oceanWhisperDarkPalette.textBody,
    },
    palette: oceanWhisperDarkPalette,
  },
  {
    id: 'forest_retreat_light',
    name: 'Forest Retreat (Light)',
    description: 'Natural greens and earthy tones for a grounded feel.',
    isDark: false,
    previewColors: {
      primary: FOREST_GREEN,
      surface: forestRetreatPalette.surface,
      background: forestRetreatPalette.background,
      textBody: forestRetreatPalette.textBody,
    },
    palette: forestRetreatPalette,
  },
   {
    id: 'forest_retreat_dark',
    name: 'Forest Retreat (Dark)',
    description: 'A rich, dark theme inspired by deep forests.',
    isDark: true,
    previewColors: {
      primary: FOREST_GREEN,
      surface: forestRetreatDarkPalette.surface,
      background: forestRetreatDarkPalette.background,
      textBody: forestRetreatDarkPalette.textBody,
    },
    palette: forestRetreatDarkPalette,
  },
  {
    id: 'monochrome_modern_light',
    name: 'Monochrome Modern (Light)',
    description: 'Sleek and minimalist, using shades of gray.',
    isDark: false,
    previewColors: {
      primary: MINIMAL_GRAY,
      surface: monochromeModernPalette.surface,
      background: monochromeModernPalette.background,
      textBody: monochromeModernPalette.textBody,
    },
    palette: monochromeModernPalette,
  },
  {
    id: 'monochrome_modern_dark',
    name: 'Monochrome Modern (Dark)',
    description: 'A sophisticated dark grayscale theme.',
    isDark: true,
    previewColors: {
      primary: MINIMAL_GRAY,
      surface: monochromeModernDarkPalette.surface,
      background: monochromeModernDarkPalette.background,
      textBody: monochromeModernDarkPalette.textBody,
    },
    palette: monochromeModernDarkPalette,
  },
];

// Helper to get a theme by ID
export const getThemeById = (themeId: string): ThemeDefinition | undefined =>
  PREDEFINED_THEMES.find(t => t.id === themeId);

// Default theme to fallback to
export const DEFAULT_THEME_ID = 'terrarium_orange_light';
export const DEFAULT_THEME = getThemeById(DEFAULT_THEME_ID) || PREDEFINED_THEMES[0];